import type { CallStats } from 'features/analytics/interfaces/';
import {
  calculateRoundedPercentage,
  roundToTwoDecimals,
} from 'common/helpers/';

export interface CallScoreMetrics {
  packetLossPercent: number;
  jitterMs: number;
  roundTripTimeMs: number;
  frameRateFps: number;
  qualityLimitationReason?: string;
}

export interface CallScoreResult {
  score: number;
  grade: string;
  metrics: CallScoreMetrics;
  breakdown: {
    packetLossScore: number;
    jitterScore: number;
    rttScore: number;
    frameRateScore: number;
    qualityLimitationPenalty: number;
  };
}

const SCORE_WEIGHTS = {
  packetLoss: 0.35,
  jitter: 0.25,
  rtt: 0.2,
  frameRate: 0.2,
};

interface CallMetricsInput {
  videoPacketsLost: number;
  audioPacketsLost: number;
  videoPacketsReceived: number;
  audioPacketsReceived: number;
  videoJitter: number;
  audioJitter: number;
  videoRtt: number;
  audioRtt: number;
  frameRate: number;
  qualityLimitationReason?: string;
}

/**
 * Calculates a comprehensive call quality score based on WebRTC statistics.
 *
 * This function analyzes various video call metrics including packet loss, jitter,
 * round-trip time, frame rate, and quality limitations to produce a composite
 * quality score from 0-100 with corresponding letter grades.
 *
 * The scoring algorithm uses weighted contributions from different metrics:
 * - Packet Loss: 35% weight
 * - Jitter: 25% weight
 * - Round Trip Time: 20% weight
 * - Frame Rate: 20% weight
 *
 * Additional penalties are applied based on quality limitation reasons
 * (CPU, bandwidth, etc.).
 *
 * @param summary - The call statistics object containing inbound/outbound RTP stats
 *                  for both audio and video streams, including packet counts,
 *                  timing metrics, and quality limitation information
 *
 * @returns A CallScoreResult object containing:
 *          - score: Overall quality score (0-100, rounded to nearest integer)
 *          - grade: Letter grade (A/B/C/D/F or I for incomplete)
 *          - metrics: Raw metric values used for diagnostics
 *          - breakdown: Individual component scores before weighting
 *
 * @example
 * ```typescript
 * const callStats: CallStats = {
 *   inboundRtpVideo: { packetsLost: 5, packetsReceived: 1000, framesPerSecond: 30 },
 *   remoteInboundRtpVideo: { jitter: 0.01, roundTripTime: 0.05 },
 *   // ... other stats
 * };
 *
 * const result = calculateCallScore(callStats);
 * console.log(`Call quality: ${result.score} (${result.grade})`);
 * ```
 *
 * @remarks
 * - Returns score of 0 with grade 'I' (Incomplete) if required video stats are missing
 * - Requires valid frame rate > 0 to calculate meaningful scores
 * - Uses only video metrics for composite scoring but shows combined audio/video
 *   packet loss in diagnostic metrics
 * - Scores are clamped between 0-100 after applying quality limitation penalties
 */
export const calculateCallScore = (summary: CallStats): CallScoreResult => {
  // Check for required video stats and valid framerate
  const videoStats = summary.inboundRtpVideo;
  const remoteVideoStats = summary.remoteInboundRtpVideo;

  const videoStatsComplete =
    hasRequiredVideoStats(summary) &&
    videoStats &&
    isStatNumber(videoStats.framesPerSecond) &&
    videoStats.framesPerSecond !== undefined &&
    videoStats.framesPerSecond > 0;

  if (!videoStatsComplete) {
    return {
      score: 0,
      grade: 'I', // Incomplete
      metrics: {
        packetLossPercent: 0,
        jitterMs: 0,
        roundTripTimeMs: 0,
        frameRateFps: 0,
        qualityLimitationReason:
          summary.outboundRtpVideo?.qualityLimitationReason,
      },
      breakdown: {
        packetLossScore: 0,
        jitterScore: 0,
        rttScore: 0,
        frameRateScore: 0,
        qualityLimitationPenalty: 0,
      },
    };
  }

  const videoPacketsLost = extractStatNumber(videoStats.packetsLost);
  const videoPacketsReceived = extractStatNumber(videoStats.packetsReceived);
  const audioPacketsLost = extractStatNumber(
    summary.inboundRtpAudio?.packetsLost
  );
  const audioPacketsReceived = extractStatNumber(
    summary.inboundRtpAudio?.packetsReceived
  );

  const videoJitter = extractStatNumber(remoteVideoStats?.jitter);
  const audioJitter = extractStatNumber(summary.remoteInboundRtpAudio?.jitter);

  const videoRtt = extractStatNumber(remoteVideoStats?.roundTripTime);
  const audioRtt = extractStatNumber(
    summary.remoteInboundRtpAudio?.roundTripTime
  );

  const frameRate = extractStatNumber(videoStats.framesPerSecond);
  const qualityLimitationReason =
    summary.outboundRtpVideo?.qualityLimitationReason;

  // Use only VIDEO scores for composite scoring
  const packetLossScore = calculatePacketLossScore(
    videoPacketsLost,
    videoPacketsReceived
  );
  const jitterScore = calculateJitterScore(videoJitter);
  const rttScore = calculateRttScore(videoRtt);

  const frameRateScore = calculateFrameRateScore(frameRate);
  const qualityLimitationPenalty = calculateQualityLimitationPenalty(
    qualityLimitationReason
  );

  const baseScore =
    packetLossScore * SCORE_WEIGHTS.packetLoss +
    jitterScore * SCORE_WEIGHTS.jitter +
    rttScore * SCORE_WEIGHTS.rtt +
    frameRateScore * SCORE_WEIGHTS.frameRate;

  const finalScore = Math.max(
    0,
    Math.min(100, baseScore - qualityLimitationPenalty)
  );

  const metrics = calculateCallMetrics({
    videoPacketsLost,
    audioPacketsLost,
    videoPacketsReceived,
    audioPacketsReceived,
    videoJitter,
    audioJitter,
    videoRtt,
    audioRtt,
    frameRate,
    qualityLimitationReason,
  });

  return {
    score: Math.round(finalScore),
    grade: getGrade(finalScore),
    metrics,
    breakdown: {
      packetLossScore: Math.round(packetLossScore),
      jitterScore: Math.round(jitterScore),
      rttScore: Math.round(rttScore),
      frameRateScore: Math.round(frameRateScore),
      qualityLimitationPenalty,
    },
  };
};

const isStatNumber = (value: any): boolean =>
  typeof value === 'number' && !isNaN(value);

const hasRequiredVideoStats = (summary: CallStats): boolean => {
  const videoStats = summary.inboundRtpVideo;
  const remoteVideoStats = summary.remoteInboundRtpVideo;

  if (!videoStats || !remoteVideoStats) {
    return false;
  }

  return (
    isStatNumber(videoStats.packetsLost) &&
    isStatNumber(videoStats.packetsReceived) &&
    isStatNumber(videoStats.framesPerSecond) &&
    isStatNumber(remoteVideoStats.jitter) &&
    isStatNumber(remoteVideoStats.roundTripTime)
  );
};

const extractStatNumber = (value: number | number[] | undefined): number => {
  if (Array.isArray(value)) {
    return value.length > 0 ? value[value.length - 1] : 0;
  }
  return value ?? 0;
};

const calculatePacketLossScore = (
  packetsLost: number,
  packetsReceived: number
): number => {
  const totalPackets = packetsLost + packetsReceived;
  if (totalPackets === 0) {
    return 100;
  }
  const packetLossRate = (packetsLost / totalPackets) * 100;

  if (packetLossRate === 0) {
    return 100;
  }
  if (packetLossRate <= 0.1) {
    return Math.max(85, 100 - packetLossRate * 150);
  }
  if (packetLossRate <= 0.5) {
    return Math.max(70, 85 - (packetLossRate - 0.1) * 37.5);
  }
  if (packetLossRate <= 1.0) {
    return Math.max(50, 70 - (packetLossRate - 0.5) * 40);
  }
  if (packetLossRate <= 2.0) {
    return Math.max(0, 50 - (packetLossRate - 1.0) * 50);
  }

  return 0;
};

const calculateJitterScore = (jitter: number): number => {
  // Convert jitter from seconds to milliseconds
  const jitterMs = jitter * 1000;

  if (jitterMs <= 10) {
    return 100;
  }
  if (jitterMs <= 20) {
    return Math.max(85, 100 - (jitterMs - 10) * 1.5);
  }
  if (jitterMs <= 40) {
    return Math.max(70, 85 - (jitterMs - 20) * 0.75);
  }
  if (jitterMs <= 80) {
    return Math.max(50, 70 - (jitterMs - 40) * 0.5);
  }

  return Math.max(0, 50 - (jitterMs - 80) * 0.25);
};

const calculateRttScore = (rtt: number): number => {
  const rttMs = rtt * 1000;

  if (rttMs <= 50) {
    return 100;
  }
  if (rttMs <= 100) {
    return Math.max(85, 100 - (rttMs - 50) * 0.3);
  }
  if (rttMs <= 200) {
    return Math.max(70, 85 - (rttMs - 100) * 0.15);
  }
  if (rttMs <= 400) {
    return Math.max(50, 70 - (rttMs - 200) * 0.1);
  }

  return Math.max(0, 50 - (rttMs - 400) * 0.05);
};

const calculateFrameRateScore = (fps: number): number => {
  if (fps >= 24) {
    return 100;
  }
  if (fps >= 20) {
    return Math.max(85, 100 - (24 - fps) * 3.75);
  }
  if (fps >= 15) {
    return Math.max(70, 85 - (20 - fps) * 3);
  }
  if (fps >= 10) {
    return Math.max(50, 70 - (15 - fps) * 4);
  }

  return Math.max(0, fps * 5);
};

const calculateQualityLimitationPenalty = (reason?: string): number => {
  if (!reason || reason === 'none') {
    return 0;
  }

  switch (reason.toLowerCase()) {
    case 'cpu':
      return 15;
    case 'bandwidth':
      return 20;
    case 'other':
      return 10;
    default:
      return 5;
  }
};

const getGrade = (score: number): string => {
  if (score >= 90) {
    return 'A';
  }
  if (score >= 80) {
    return 'B';
  }
  if (score >= 70) {
    return 'C';
  }
  if (score >= 60) {
    return 'D';
  }
  return 'F';
};

const calculateCallMetrics = (input: CallMetricsInput): CallScoreMetrics => {
  const {
    videoPacketsLost,
    audioPacketsLost,
    videoPacketsReceived,
    audioPacketsReceived,
    videoJitter,
    audioJitter,
    videoRtt,
    audioRtt,
    frameRate,
    qualityLimitationReason,
  } = input;

  // Calculate packet loss percentage from combined audio/video streams
  const totalPacketsLost = videoPacketsLost + audioPacketsLost;
  const totalPacketsReceived = videoPacketsReceived + audioPacketsReceived;
  const totalPacketsTransmitted = totalPacketsLost + totalPacketsReceived;

  const packetLossPercent =
    totalPacketsTransmitted > 0
      ? calculateRoundedPercentage(totalPacketsLost, totalPacketsTransmitted)
      : 0;

  // Calculate average jitter from video and audio streams, convert seconds to milliseconds
  const averageJitterSeconds = (videoJitter + audioJitter) / 2;
  const averageJitterMilliseconds = averageJitterSeconds * 1000;
  const jitterMs = roundToTwoDecimals(averageJitterMilliseconds);

  // Calculate average round trip time from video and audio streams, convert seconds to milliseconds
  const averageRttSeconds = (videoRtt + audioRtt) / 2;
  const averageRttMilliseconds = averageRttSeconds * 1000;
  const roundTripTimeMs = roundToTwoDecimals(averageRttMilliseconds);

  // Round frame rate to two decimal places for consistent display
  const frameRateFps = roundToTwoDecimals(frameRate);

  return {
    packetLossPercent,
    jitterMs,
    roundTripTimeMs,
    frameRateFps,
    qualityLimitationReason,
  };
};
